<template>
  <b-sidebar
    id="sidebar-snapshot-change-request"
    sidebar-class="sidebar-lg"
    :visible="requestSidebar.visible"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @change="onVisibilityChange"
  >
    <template #default="{ hide }">
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h4 class="mb-0">
          Solicitação #{{ request.id }}
          <b-badge :variant="statusVariant(request.status)" class="ml-50">
            {{ statusLabel(request.status) }}
          </b-badge>
        </h4>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <retroactive-spread-suggestion
        v-if="retroactivePreview"
        :preview="retroactivePreview"
        :contract-id="request.contract_id"
        @close="retroactivePreview = null"
      />

      <div v-else-if="request.id" class="p-2 pb-3">
        <dl class="row mb-0">
          <dt class="col-5">Contrato</dt>
          <dd class="col-7">{{ request.contract_id }}</dd>

          <dt class="col-5">Solicitante</dt>
          <dd class="col-7">
            {{ request.requested_by_name || "—" }}
            <small v-if="request.requested_by_role_name" class="text-muted d-block">
              {{ request.requested_by_role_name }}
            </small>
          </dd>

          <dt class="col-5">Solicitado em</dt>
          <dd class="col-7">{{ dateTime(request.created_at) }}</dd>

          <dt v-if="request.reviewed_by_name" class="col-5">Decidido por</dt>
          <dd v-if="request.reviewed_by_name" class="col-7">
            {{ request.reviewed_by_name }}
            <b-badge v-if="request.self_approved" variant="light-warning" class="ml-50">
              Autoaprovação
            </b-badge>
          </dd>
        </dl>

        <h6 class="mt-2">Motivo</h6>
        <p class="mb-0">{{ request.reason }}</p>

        <template v-if="request.expected_impact">
          <h6 class="mt-2">Impacto esperado (declarado)</h6>
          <p class="mb-0">{{ request.expected_impact }}</p>
        </template>

        <h6 class="mt-2">Distribuição</h6>
        <b-table-simple small responsive class="mb-0">
          <b-thead>
            <b-tr>
              <b-th>PN</b-th>
              <b-th class="text-right">Atual</b-th>
              <b-th class="text-right">Pretendido</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="line in distributionDiff" :key="line.key">
              <b-td>
                {{ line.name }}
                <small class="text-muted d-block">{{ line.role }}</small>
              </b-td>
              <b-td class="text-right">{{ percentage(line.current) }}</b-td>
              <b-td
                class="text-right"
                :class="{ 'font-weight-bold text-primary': line.changed }"
              >
                {{ percentage(line.proposed) }}
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot>
            <b-tr>
              <b-th>Total</b-th>
              <b-th class="text-right">{{ percentage(currentTotal) }}</b-th>
              <b-th class="text-right">
                {{ percentage(request.proposed_total_percentage) }}
              </b-th>
            </b-tr>
          </b-tfoot>
        </b-table-simple>
        <b-alert
          v-if="!isHundred(request.proposed_total_percentage)"
          variant="warning"
          show
          class="mt-1 mb-0"
        >
          <div class="alert-body">
            <small>
              A distribuição pretendida soma
              {{ percentage(request.proposed_total_percentage) }}. O que não for distribuído fica
              com a Futuro.
            </small>
          </div>
        </b-alert>

        <b-alert v-if="request.review_notes" variant="secondary" show class="mt-1 mb-0">
          <div class="alert-body">
            <small><strong>Motivo da decisão:</strong> {{ request.review_notes }}</small>
          </div>
        </b-alert>

        <b-alert v-if="request.is_stale" variant="warning" show class="mt-2 mb-0">
          <div class="alert-body">
            <small>
              O snapshot deste contrato mudou depois que a solicitação foi aberta. Ela não
              pode ser aprovada: peça uma nova a partir do estado atual.
            </small>
          </div>
        </b-alert>

        <h6 class="mt-2">Histórico da solicitação</h6>
        <ul class="list-unstyled mb-0">
          <li v-for="item in timeline" :key="item.label" class="mb-1">
            <b-badge :variant="item.variant">{{ item.label }}</b-badge>
            <small class="text-muted ml-50">
              {{ dateTime(item.at) }}
              ·
              {{ item.who || "—" }}
            </small>
          </li>
        </ul>

        <template v-if="canDecide">
          <hr class="my-2" />
          <b-form-group label="Motivo (obrigatório para rejeitar)" label-for="review-notes">
            <b-form-textarea
              id="review-notes"
              v-model="reviewNotes"
              rows="3"
              :state="rejectAttempted && !notesAreValid ? false : null"
              placeholder="Descreva o motivo da rejeição"
            />
            <b-form-invalid-feedback :state="rejectAttempted && !notesAreValid ? false : null">
              Descreva o motivo com pelo menos 10 caracteres.
            </b-form-invalid-feedback>
          </b-form-group>

          <div class="d-flex mt-2">
            <b-button
              :disabled="saving"
              variant="primary"
              class="mr-2"
              @click="confirmApprove"
            >
              {{ saving ? "Processando..." : "Aprovar e aplicar" }}
            </b-button>
            <b-button :disabled="saving" variant="outline-danger" @click="onReject">
              Rejeitar
            </b-button>
          </div>
        </template>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BBadge,
  BButton,
  BAlert,
  BFormGroup,
  BFormTextarea,
  BFormInvalidFeedback,
  BTableSimple,
  BThead,
  BTbody,
  BTfoot,
  BTr,
  BTh,
  BTd,
} from "bootstrap-vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { contractPermissions } from "@/mixins/contractPermissions";
import useAppConfig from "@core/app-config/useAppConfig";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { apiErrorToast } from "@/helpers/error_messages";
import {
  REQUEST_STATUS,
  statusLabel,
  statusVariant,
} from "@/constants/snapshot_change";
import RetroactiveSpreadSuggestion from "@/modules/contract/components/RetroactiveSpreadSuggestion";
import * as contractTypes from "@/modules/contract/store/types";
import * as types from "../store/types";

export default {
  components: {
    BSidebar,
    BBadge,
    BButton,
    BAlert,
    BFormGroup,
    BFormTextarea,
    BFormInvalidFeedback,
    BTableSimple,
    BThead,
    BTbody,
    BTfoot,
    BTr,
    BTh,
    BTd,
    RetroactiveSpreadSuggestion,
  },
  data() {
    return {
      saving: false,
      reviewNotes: "",
      rejectAttempted: false,
      retroactivePreview: null,
    };
  },
  mixins: [contractPermissions],
  computed: {
    ...mapGetters({
      requestSidebar: types.REQUEST_SIDEBAR,
    }),
    timeline() {
      const items = [
        {
          label: "Solicitada",
          who: this.request.requested_by_name,
          at: this.request.created_at,
          variant: "light-secondary",
        },
      ];

      if (this.request.reviewed_at) {
        items.push({
          label:
            this.request.status === REQUEST_STATUS.APPROVED
              ? "Aprovada e snapshot alterado"
              : "Rejeitada",
          who: this.request.reviewed_by_name,
          at: this.request.reviewed_at,
          variant: statusVariant(this.request.status),
        });
      }

      return items;
    },
    canDecide() {
      return (
        this.canApproveSnapshotChange &&
        this.request.status === REQUEST_STATUS.PENDING
      );
    },
    request() {
      return this.requestSidebar.request || {};
    },
    notesAreValid() {
      return (this.reviewNotes || "").trim().length >= 10;
    },
    currentTotal() {
      return (this.request.current_distribution || []).reduce(
        (total, line) => total + Number(line.percentage_value || 0),
        0
      );
    },
    distributionDiff() {
      const current = this.request.current_distribution || [];
      const proposed = this.request.proposed_distribution || [];
      const keyOf = (line) =>
        line.id
          ? `id:${line.id}`
          : `new:${line.id_consultant}:${line.id_consultant_role}`;

      const rows = new Map();

      current.forEach((line) => {
        rows.set(keyOf(line), {
          key: keyOf(line),
          name: line.consultant_name || `PN ${line.id_consultant}`,
          role: line.consultant_role_name || `Nível ${line.id_consultant_role}`,
          current: Number(line.percentage_value || 0),
          proposed: 0,
        });
      });

      proposed.forEach((line) => {
        const key = keyOf(line);
        const existing = rows.get(key);

        if (existing) {
          existing.proposed = Number(line.percentage_value || 0);
          return;
        }

        rows.set(key, {
          key,
          name: line.consultant_name || `PN ${line.id_consultant}`,
          role: line.consultant_role_name || `Nível ${line.id_consultant_role}`,
          current: 0,
          proposed: Number(line.percentage_value || 0),
        });
      });

      return Array.from(rows.values()).map((row) => ({
        ...row,
        changed: Math.abs(row.current - row.proposed) > 0.0001,
      }));
    },
  },
  setup() {
    const { skin } = useAppConfig();

    return { toast: useToast(), skin };
  },
  methods: {
    ...mapMutations({
      mutateRequestSidebar: types.MUTATE_REQUEST_SIDEBAR,
    }),
    ...mapActions({
      updateRequestStatus: types.UPDATE_REQUEST_STATUS,
      getRetroactivePreview: contractTypes.GET_RETROACTIVE_SPREAD_PREVIEW,
    }),
    statusLabel,
    statusVariant,
    onVisibilityChange(visible) {
      this.mutateRequestSidebar({ visible });

      if (!visible) {
        this.retroactivePreview = null;
        this.reviewNotes = "";
        this.rejectAttempted = false;
      }
    },
    confirmApprove() {
      this.$swal({
        title: "Confirmação",
        text:
          "Aprovar grava a nova divisão de comissão do contrato imediatamente e recalcula as "
          + "comissões ainda não faturadas. Deseja continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Aprovar e aplicar",
        cancelButtonText: "Cancelar",
        background: this.skin === "dark" ? "#283046" : "",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: this.skin === "dark" ? "text-white" : "",
        },
      }).then((result) => {
        if (result.value) this.onApprove();
      });
    },
    onApprove() {
      this.saving = true;

      this.updateRequestStatus({
        id: this.request.id,
        status: REQUEST_STATUS.APPROVED,
        reviewNotes: this.reviewNotes,
      })
        .then((response) => {
          this.mutateRequestSidebar({ request: response.data });
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Solicitação aprovada e snapshot alterado.",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.$emit("decided");
          this.offerRetroactiveAdjustment();
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Não foi possível aprovar a solicitação. Entre em contato com o setor de TI."
            ),
          });
          this.$emit("decided");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    offerRetroactiveAdjustment() {
      this.getRetroactivePreview({ contractId: this.request.contract_id })
        .then((response) => {
          if (response.data && response.data.has_suggestion) {
            this.retroactivePreview = response.data;
          }
        })
        .catch(() => {
          this.retroactivePreview = null;
        });
    },
    onReject() {
      this.rejectAttempted = true;

      if (!this.notesAreValid) return;

      this.saving = true;

      this.updateRequestStatus({
        id: this.request.id,
        status: REQUEST_STATUS.REJECTED,
        reviewNotes: this.reviewNotes,
      })
        .then((response) => {
          this.mutateRequestSidebar({ request: response.data });
          this.reviewNotes = "";
          this.rejectAttempted = false;
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Solicitação rejeitada. O snapshot não foi alterado.",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.$emit("decided");
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Não foi possível rejeitar a solicitação. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    isHundred(value) {
      return Math.abs(Number(value) - 100) <= 0.0001;
    },
    percentage(value) {
      return `${Number(value || 0).toFixed(2).replace(".", ",")}%`;
    },
    dateTime(value) {
      if (!value) return "—";

      return new Date(value).toLocaleString("pt-BR");
    },
  },
};
</script>
